<template>
<div class="hris-page">
	<div class="hris-page-inner">
		<div class="hris-page-header">
			<span class="hris-page-eyebrow">System Settings</span>
			<h1 class="hris-page-title">Payroll Groups</h1>
			<p class="hris-page-subtitle">Define labels to be used for multiple pay days per cut-off.</p>
		</div>

		<q-form ref="vForm" @submit="onSubmit">
			<div class="row q-col-gutter-lg">
				<div class="col-12 col-md-4">
					<div class="hris-form-section">
						<h2 class="hris-form-section-title">{{ tempID === 0 ? 'Create' : 'Edit' }} Group</h2>
						<div class="hris-form-row">
							<div class="hris-form-field">
								<q-input
									outlined
									dense
									color="green"
									v-model="name"
									label="Group Name / Label"
									hint="e.g. Staff, Managers, Batch 1"
									:rules="[formRules.required]"
									class="caps"
								></q-input>
							</div>
						</div>
						<div class="hris-form-row">
							<div class="hris-form-field">
								<label class="hris-form-label">Status</label>
								<div class="q-gutter-sm">
									<q-radio keep-color v-model="is_active" val="Y" label="Active" color="green"></q-radio>
									<q-radio keep-color v-model="is_active" val="N" label="Inactive" color="red"></q-radio>
								</div>
							</div>
						</div>
						<div class="hris-form-actions" style="flex-direction: column;">
							<q-btn
								unelevated
								class="hris-q-primary full-width"
								type="submit"
								:label="tempID === 0 ? 'Save Group' : 'Update Group'"
								:loading="spinner"
							></q-btn>
							<q-btn
								v-if="tempID !== 0"
								flat
								class="full-width hris-q-ghost"
								label="Cancel Edit"
								@click="resetForm"
							></q-btn>
						</div>
					</div>
				</div>
				<div class="col-12 col-md-8">
					<div class="hris-panel">
						<h2 class="hris-panel-title">Payroll Groups List</h2>
						<q-table
							flat
							:data="data"
							:columns="columns"
							row-key="id"
							:pagination.sync="pagination"
							:loading="spinner"
						>
							<template v-slot:body="props">
								<q-tr :props="props" @click="selectedRow(props)" style="cursor: pointer;">
									<q-td key="counter" :props="props">{{ props.row.counter }}</q-td>
									<q-td key="name" :props="props">
										<div class="text-weight-bold">{{ props.row.name }}</div>
									</q-td>
									<q-td key="is_active" :props="props">
										<span class="hris-badge" :class="props.row.is_active === 'Y' ? 'hris-badge-success' : 'hris-badge-danger'">
											{{ props.row.is_active === 'Y' ? 'Active' : 'Inactive' }}
										</span>
									</q-td>
									<q-td key="action" :props="props">
										<q-btn
											round
											flat
											dense
											class="hris-q-ghost"
											icon="fas fa-trash"
											@click.stop="removeRow(props)"
										>
											<q-tooltip>Remove Group</q-tooltip>
										</q-btn>
									</q-td>
								</q-tr>
							</template>
							<template v-slot:no-data="">
								<div class="hris-empty full-width">
									No payroll groups found.
								</div>
							</template>
						</q-table>
					</div>
				</div>
			</div>
		</q-form>

		<q-dialog v-model="deleted" persistent>
			<div class="hris-modal" style="max-width: 420px;">
				<div class="hris-modal-header">
					<h2 class="hris-modal-title">Delete Payroll Group?</h2>
				</div>
				<div class="hris-modal-body">
					Are you sure you want to permanently delete this group? This may affect payroll processing if the label is already in use.
				</div>
				<div class="hris-modal-footer">
					<q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
					<q-btn unelevated label="Delete" class="hris-q-primary" v-close-popup @click="deletionConfirmation"></q-btn>
				</div>
			</div>
		</q-dialog>
	</div>
</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {
	mixins: [mixins],
	data () {
		return {
			tempID: 0,
			spinner: false,
			deleted: false,
			name: null,
			is_active: 'Y',
			data: [],
			type: 'PAYROLL_GROUP',
			columns: [
				{ name: 'counter', align: 'left', label: '#', field: 'counter', sortable: true },
				{ name: 'name', align: 'left', label: 'Group Name', field: 'name', sortable: true },
				{ name: 'is_active', align: 'center', label: 'Status', field: 'is_active', sortable: true },
				{ name: 'action', align: 'center', label: 'Action', field: '' },
			],
			pagination: {
				rowsPerPage: 15
			},
		}
	},

	created(){
		this.displayData()
	},

	methods: {
		onSubmit: function(){
			this.spinner = true;
			let formData = new FormData();
			formData.append("name", this.name);
			formData.append("type", this.type);
			formData.append("status", this.is_active);
			formData.append("tempID", this.tempID);

			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/admin/setting/relationship', formData)
			.then((res) => {
				this.$q.notify({
					color: 'green-9',
					message: this.tempID === 0 ? 'Group created' : 'Group updated'
				});
				this.displayData();
				this.resetForm();
			})
			.catch(err => {
				this.spinner = false;
				this.$q.notify({ color: 'red', message: 'Failed to save group' });
			});
		},

		resetForm() {
			this.name = '';
			this.is_active = 'Y';
			this.tempID = 0;
			this.spinner = false;
			if (this.$refs.vForm) this.$refs.vForm.reset();
		},

		displayData: function() {
			this.spinner = true;
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			})
			.get(`/admin/setting/${this.type}`)
			.then((res) => {
				this.data = res.data.data || [];
				this.spinner = false;
			})
			.catch(() => {
				this.spinner = false;
			});
		},

		selectedRow: function(obj){
			this.name = obj.row.name;
			this.is_active = obj.row.is_active;
			this.tempID = obj.row.id;
		},

		removeRow: function(obj){
			this.deleted = true;
			this.tempID = obj.row.id;
		},

		deletionConfirmation: function(){
			this.spinner = true;
			let formData = new FormData();
			formData.append("tempID", this.tempID);
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/admin/setting/delete', formData)
			.then((res) => {
				this.$q.notify({ color: 'orange-9', message: 'Group deleted' });
				this.tempID = 0;
				this.displayData();
			});
		}
	}
}
</script>

<style scoped>
.caps {
	text-transform: uppercase;
}
</style>
